import React from "react";

import { faSpinnerThird } from "@fortawesome/pro-regular-svg-icons/faSpinnerThird";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import notification from "antd/es/notification";
import { defineMessage, defineMessages } from "react-intl";
import { put, select, takeLatest } from "redux-saga/effects";

import { GeocodeResult } from "@mapmycustomers/shared/types/base/Located";
import {
  AnyEntityId,
  EntitiesSupportingRoutes,
  EntityType,
} from "@mapmycustomers/shared/types/entity";
import Route, {
  CreateRoutePayload,
  CreateRouteStopItem,
  RoutePreview,
  RouteStopBulkItem,
} from "@mapmycustomers/shared/types/entity/Route";
import Organization from "@mapmycustomers/shared/types/Organization";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import i18nService from "@app/config/I18nService";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getOrganization } from "@app/store/iam";
import { routeValidationErrorMessages } from "@app/store/routes/messages";
import { notifyAboutChanges } from "@app/store/uiSync/actions";
import buildPreviewPayloadFromRoute from "@app/util/route/buildPreviewPayloadFromRoute";
import getRoutePreviewMetrics from "@app/util/route/routePreviewMetrics";

import { addToRoute, createRoute, filterRoutes, initialize } from "./actions";
import styles from "./saga.module.scss";
import { getAutoAssignOnCreate, getEntities, getSelectedRouteId } from "./selectors";

const routeAddedSuccessMessages = defineMessages({
  alreadyInRoute: {
    id: "addToRouteModal.alreadyInRoute.infoMessage",
    defaultMessage: "{entityName} {count, plural, one {is} other {are}} already in this Route",
    description: "Info message when selected records are already in the chosen route",
  },
  existingRoute: {
    id: "addToRouteModal.existingRoute.successMessage",
    defaultMessage: "{entityName} successfully added to Route",
    description: "Route is updated successfully in the Add to Route modal",
  },
  newRoute: {
    id: "addToRouteModal.newRoute.successMessage",
    defaultMessage: "{entityName} successfully added to newly created Route",
    description: "Route is updated successfully in the Add to Route modal",
  },
  recordEntityName: {
    id: "addToRouteModal.recordEntityName",
    defaultMessage: "{count, plural, one {Record} other {Records}}",
    description: "Generic record label used in Add To Route notifications",
  },
});

const failedGeocodeStartAddressMessage = defineMessage({
  id: "createRouteModal.error.failedGeocodeStart",
  defaultMessage: "Failed to find starting location",
  description: "Failed to find starting location in Create New Route modal",
});

const failedGeocodeEndAddressMessage = defineMessage({
  id: "createRouteModal.error.failedGeocodeEnd",
  defaultMessage: "Failed to find ending location",
  description: "Failed to find ending location in Create New Route modal",
});

export function* onInitialize() {
  try {
    const org: Organization = yield select(getOrganization);
    const routes: ListResponse<Route> = yield callApi("fetchUnifiedRoutes", org.id, {
      $filters: { includeAccessStatus: true },
      $limit: 100,
      $order: ["name"],
    });

    yield put(initialize.success({ routes: routes.data }));
  } catch (error) {
    yield put(initialize.failure());
    yield put(handleError({ error }));
  }
}

export function* onAddToRoute({ payload }: ReturnType<typeof addToRoute.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const selectedRouteId: Route["id"] | undefined = payload.routeId
      ? payload.routeId
      : yield select(getSelectedRouteId);
    if (!selectedRouteId) {
      return;
    }

    const entityIds = payload.entities.map(({ id }) => id);

    const route: Route = yield callApi("fetchUnifiedRoute", org.id, selectedRouteId);

    // existing-stop keys, type-prefixed; manual/deal stops contribute no key
    const existingKeys = new Set<string>();
    (route.stopTime ?? []).forEach((stop) => {
      if (stop.entity === EntityType.COMPANY) {
        existingKeys.add(`${EntityType.COMPANY}:${stop.accountId}`);
      } else if (stop.entity === EntityType.PERSON) {
        existingKeys.add(`${EntityType.PERSON}:${stop.contactId}`);
      }
    });
    const newEntities = payload.entities.filter(
      (entity) => !existingKeys.has(`${entity.entity}:${entity.id}`)
    );

    if (newEntities.length === 0) {
      // every selected record already in this route — nothing to send
      if (payload.showNotification !== false) {
        const intl = i18nService.getIntl();
        if (intl) {
          notification.info({
            message: intl.formatMessage(routeAddedSuccessMessages.alreadyInRoute, {
              count: entityIds.length,
              entityName: intl.formatMessage(routeAddedSuccessMessages.recordEntityName, {
                count: entityIds.length,
              }),
            }),
          });
        }
      }
      payload.callback(true);
      yield put(addToRoute.success());
      return;
    }

    const allottedTime = route.routeDetail.allottedTime ?? 0;
    const newStopItems: CreateRouteStopItem[] = newEntities.map((entity) =>
      entity.entity === EntityType.COMPANY
        ? { accountId: entity.id, allottedTime }
        : { allottedTime, contactId: entity.id }
    );
    // Build the existing-stop portion through the shared helper so the checked-off filter
    // and the routeDetail shaping both come from one place, same as bulkAddEntitiesToRoutes
    // (component/view/SelectBar/store/saga.ts).
    const basePayload = buildPreviewPayloadFromRoute(route);
    const allStopItems: CreateRouteStopItem[] = [...basePayload.stopTime, ...newStopItems];

    const preview: RoutePreview = yield callApi("previewUnifiedRoute", org.id, {
      routeDetail: basePayload.routeDetail,
      stopTime: allStopItems,
    });

    const appendOps: RouteStopBulkItem[] = newEntities.map((entity) =>
      entity.entity === EntityType.COMPANY
        ? { _action_: "c", accountId: entity.id, allottedTime }
        : { _action_: "c", allottedTime, contactId: entity.id }
    );

    yield callApi(
      "bulkUpdateUnifiedRouteStops",
      org.id,
      selectedRouteId,
      appendOps,
      getRoutePreviewMetrics(preview)
    );

    if (payload.showNotification !== false) {
      const intl = i18nService.getIntl();
      if (intl) {
        notification.success({
          message: intl.formatMessage(routeAddedSuccessMessages.existingRoute, {
            count: entityIds.length,
            entityName: intl.formatMessage(routeAddedSuccessMessages.recordEntityName, {
              count: entityIds.length,
            }),
          }),
        });
      }
    }

    payload.callback(true);
    yield put(addToRoute.success());
    yield put(notifyAboutChanges({ entityType: EntityType.ROUTE, updatedIds: [selectedRouteId] }));
  } catch (error) {
    yield put(addToRoute.failure());
    yield put(handleError({ error, messageByErrorCodeMap: routeValidationErrorMessages }));
  }
}

export function* onCreateRoute({ payload }: ReturnType<typeof createRoute.request>) {
  const key = `createRouteNotification_${payload.route.name}`;
  try {
    const org: Organization = yield select(getOrganization);
    const intl = i18nService.getIntl();
    const { callback, route } = payload;

    let startPoint: Pick<GeocodeResult, "geoPoint">;
    try {
      startPoint = route.routeDetail.startGeoPoint
        ? { geoPoint: route.routeDetail.startGeoPoint }
        : yield callApi("geocodeAddress", org.id, { address: route.routeDetail.startAddress });
    } catch {
      notification.error({ message: intl?.formatMessage(failedGeocodeStartAddressMessage) });
      yield put(createRoute.failure());
      return;
    }

    let endPoint: Pick<GeocodeResult, "geoPoint"> | undefined;
    if (route.routeDetail.endAddress) {
      try {
        endPoint = route.routeDetail.endGeoPoint
          ? { geoPoint: route.routeDetail.endGeoPoint }
          : yield callApi("geocodeAddress", org.id, { address: route.routeDetail.endAddress });
      } catch {
        notification.error({ message: intl?.formatMessage(failedGeocodeEndAddressMessage) });
        yield put(createRoute.failure());
        return;
      }
    }
    const autoAssignOnCreate: boolean = yield select(getAutoAssignOnCreate);
    const entities: EntitiesSupportingRoutes[] = yield select(getEntities);
    const entityIds: Array<AnyEntityId> = entities.map(({ id }) => id);

    const createRouteDetail: CreateRoutePayload["routeDetail"] = {
      allottedTime: route.routeDetail.allottedTime,
      startAddress: route.routeDetail.startAddress,
      startAt: route.routeDetail.startAt,
      startGeoPoint: startPoint.geoPoint!,
      travelMode: route.routeDetail.travelMode,
      type: route.routeDetail.type,
      ...(endPoint?.geoPoint
        ? { endAddress: route.routeDetail.endAddress ?? undefined, endGeoPoint: endPoint.geoPoint }
        : {}),
    };

    const stopTimeItems: CreateRouteStopItem[] = entities.map((entity) =>
      entity.entity === EntityType.COMPANY
        ? { accountId: entity.id, allottedTime: route.routeDetail.allottedTime }
        : { allottedTime: route.routeDetail.allottedTime, contactId: entity.id }
    );
    const stopItems: RouteStopBulkItem[] = entities.map((entity) =>
      entity.entity === EntityType.COMPANY
        ? { _action_: "c", accountId: entity.id, allottedTime: route.routeDetail.allottedTime }
        : { _action_: "c", allottedTime: route.routeDetail.allottedTime, contactId: entity.id }
    );

    let preview: RoutePreview | undefined;
    if (autoAssignOnCreate) {
      //Info message to show user that we are adding the record to route after creating the route
      notification.info({
        icon: <FontAwesomeIcon className={styles.notificationLoader} icon={faSpinnerThird} spin />,
        key,
        message: intl?.formatMessage({
          id: "createRouteModal.buildingRoute",
          defaultMessage: "Building route...",
          description: "message for build route when creating new route",
        }),
      });

      preview = yield callApi("previewUnifiedRoute", org.id, {
        routeDetail: createRouteDetail,
        stopTime: stopTimeItems,
      });
    }

    //Create Route
    const createdRoute: Route = yield callApi("createUnifiedRoute", org.id, {
      name: route.name,
      routeDetail: createRouteDetail,
    } satisfies CreateRoutePayload);

    if (autoAssignOnCreate) {
      yield callApi(
        "bulkUpdateUnifiedRouteStops",
        org.id,
        createdRoute.id,
        stopItems,
        getRoutePreviewMetrics(preview!)
      );

      notification.success({
        key,
        message: intl?.formatMessage(routeAddedSuccessMessages.newRoute, {
          entityName: intl?.formatMessage(routeAddedSuccessMessages.recordEntityName, {
            count: entityIds.length,
          }),
        }),
      });
    }

    yield put(createRoute.success(createdRoute));
    yield put(notifyAboutChanges({ added: [createdRoute], entityType: EntityType.ROUTE }));
    callback?.(createdRoute);
  } catch (error) {
    notification.close(key);
    yield put(createRoute.failure());
    yield put(handleError({ error }));
  }
}

export function* onFilterRoutes({ payload }: ReturnType<typeof filterRoutes.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const routes: ListResponse<Route> = yield callApi("fetchUnifiedRoutes", org.id, {
      $filters: {
        includeAccessStatus: true,
        name: payload.query ? { $in: payload.query } : undefined,
      },
      $limit: 100,
      $order: ["name"],
    });
    yield put(filterRoutes.success(routes.data));
  } catch (error) {
    yield put(filterRoutes.failure());
    yield put(handleError({ error }));
  }
}

export function* addToRouteModalSaga() {
  yield takeLatest(initialize.request, onInitialize);
  yield takeLatest(addToRoute.request, onAddToRoute);
  yield takeLatest(createRoute.request, onCreateRoute);
  yield takeLatest(filterRoutes.request, onFilterRoutes);
}
